import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { computeQualityScore } from '../lib/listingQuality';

export function useDebouncedValue(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function matchesSearch(listing, searchTerm) {
  if (!searchTerm || searchTerm.trim() === '') return true;
  const term = searchTerm.toLowerCase().trim();
  const fields = [
    listing.title,
    listing.city,
    listing.address,
    listing.district,
    listing.area,
    listing.neighborhood,
    listing.description,
    listing.region,
    listing.type,
    listing.agency_name,
  ];
  return fields.some(field => field && String(field).toLowerCase().includes(term));
}


export function useListings() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('listings')
      .select('*')
      .order('id', { ascending: true });

    if (error) {
      setError(error.message);
    } else {
      // Hide archived + soft-deleted listings from public consumers.
      const visible = data.filter(row => row.status !== 'archived' && row.status !== 'deleted');
      setListings(visible.map(row => mapListing(row)));
    }
    setLoading(false);
  };

  return { listings, loading, error, refetch: fetchListings };
}

export function useListing(id) {
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data, error: listErr } = await supabase
        .from('listings')
        .select('*')
        .eq('id', id)
        .single();
      if (cancelled) return;
      if (listErr) {
        setError(listErr.message);
        setLoading(false);
        return;
      }
      // Treat soft-deleted / archived as "not found" so PropertyDetailPage
      // renders its 404 panel instead of a stale listing.
      if (data?.status === 'deleted' || data?.status === 'archived') {
        setListing(null);
        setLoading(false);
        return;
      }

      // Resolve display name from profiles.user_id (single source of truth).
      // listings.contact_name is kept for back-compat but the displayed seller
      // name follows the current profile, so name changes propagate.
      let ownerProfile = null;
      if (data?.user_id) {
        const { data: prof } = await supabase
          .from('profiles')
          .select('full_name, first_name, last_name, avatar_url')
          .eq('id', data.user_id)
          .maybeSingle();
        if (!cancelled && prof) ownerProfile = prof;
      }
      if (cancelled) return;
      setListing(mapListing(data, ownerProfile));
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [id]);

  return { listing, loading, error };
}

function resolveOwnerDisplayName(ownerProfile, fallback) {
  if (!ownerProfile) return fallback;
  const full = (ownerProfile.full_name || '').trim();
  if (full) return full;
  const first = (ownerProfile.first_name || '').trim();
  const last  = (ownerProfile.last_name  || '').trim();
  const combined = `${first} ${last}`.trim();
  return combined || fallback;
}

// Maps Supabase DB columns → app shape. contact_phone / contact_email are
// present in the payload for everyone (data-layer gating is a separate task);
// PropertyDetailPage renders the phone via <MaskedPhone> which hides it for
// logged-out viewers.
function mapListing(row, ownerProfile = null) {
  const displayName = resolveOwnerDisplayName(ownerProfile, row.contact_name) || 'Property Owner';
  const listing = {
    id:               row.id,
    title:            row.title,
    price:            row.price,
    priceType:        row.price_label,
    type:             row.type,
    bedrooms:         row.beds,
    bathrooms:        row.baths,
    size:             row.sqm,
    plotSize:         row.plot_size,
    coveredArea:      row.covered_area,
    city:             row.city,
    region:           row.area,
    address:          row.address,
    lat:              row.lat  ? parseFloat(row.lat)  : null,
    lng:              row.lng  ? parseFloat(row.lng)  : null,
    show_map:         row.show_map ?? true,
    description:      row.description,
    features:         row.features || [],
    is_boosted:       row.is_boosted || false,
    boost_expires_at: row.boost_expires_at || null,
    is_partner:       row.is_partner || false,
    status:           row.status || 'active',
    sold_at:          row.sold_at || null,
    image:            row.images?.[0] || null,
    images:           row.images || [],
    badge:            row.is_featured ? 'Featured' : null,
    isFeatured:       row.is_featured,
    gradient:         '#F2F1ED',
    user_id:          row.user_id,
    contact_name:     displayName,
    contact_phone:    row.contact_phone,
    contact_email:    row.contact_email,
    agency_name:      row.agency_name,
    epc_rating:       row.epc_rating || null,
    created_at:       row.created_at || null,
    owner_avatar_url: ownerProfile?.avatar_url || null,
    agent: {
      name:   displayName,
      phone:  row.contact_phone  || null,
      agency: row.agency_name    || 'Private Seller',
    },
  };
  listing.qualityScore = computeQualityScore(listing);
  return listing;
}
